import { postJSON } from "../api.js";
import { startNewGame } from "../app/onboarding.js";
import { esc } from "../ui/format.js";
import { uiCard, uiGet, uiKpis, uiMoney } from "../ui/kit.js";
import { toast } from "../ui/modals.js";
import { emAct, emSlider, emSliderValue, emWireSliders } from "./empire.js";

/**
 * Career (backend/career.py): the serial entrepreneur. In the CEO Office:
 * every company you ran, the money and traits you carry, and the exits open
 * to you now (buyers for the company; stepping down after an IPO).
 *
 * On the end screen: an exit gets its own story and "start your next
 * company" (choose how much of your wealth to put in); any ending offers the
 * Time Machine (timemachine.js) to go back and try another road.
 */

const KIND = { takeover: "Taken over", sale: "Sold", ipo_step_down: "Stepped down after the IPO" };

function crRuns(car) {
  if (!car.runs.length) return `<p class="sx-card-sub">No exits yet. Sell a company, accept a bid, or step down after an IPO, and your career begins.</p>`;
  return `<ol class="cr-runs">${car.runs.map((r, i) => `<li><span class="cr-n">${i + 1}</span>
    <div><b>${esc(r.company)}</b> <span class="lg-chip">${esc(KIND[r.kind] || r.kind)}</span>
    <p>${esc(r.industry)} · ${esc(r.country)} · ${r.months} months · peak ${uiMoney(r.peak_valuation, { compact: true })}${r.buyer ? ` · to ${esc(r.buyer)}` : ""}</p></div>
    <em>${uiMoney(r.payout, { compact: true })}</em></li>`).join("")}</ol>`;
}

function crCarry(car) {
  const w = car.wallet;
  return `<ul class="cr-carry">
    <li>💵 <b>${uiMoney(w.cash, { compact: true })}</b> of your own money</li>
    ${(w.holdings || []).map((h) => `<li>📈 ${h.pct}% of <b>${esc(h.company)}</b> (worth ${uiMoney(h.value, { compact: true })} when you left)</li>`).join("")}
    ${(w.items || []).length ? `<li>🛍️ ${w.items.map((i) => `${i.icon} ${esc(i.name)}`).join(", ")}</li>` : ""}
    ${w.foundation ? `<li>🤲 ${esc(w.foundation.name)} (${uiMoney(w.foundation.endowment, { compact: true })})</li>` : ""}
    ${car.trait_info.map((t) => `<li>${t.icon || "👑"} <b>${esc(t.name || t.key)}</b>: ${esc(t.text || "")}</li>`).join("")}
    <li>⭐ Reputation: brand +${car.reputation.brand}, investors +${car.reputation.sentiment} at the start</li></ul>`;
}

async function renderCareerInto(host) {
  const car = await uiGet("/career");
  const now = car.now || {};
  host.innerHTML = `
    ${uiKpis([
      { label: "Exits", value: `${car.exits}` },
      { label: "Earned from exits", value: uiMoney(car.total_earned, { compact: true }) },
      { label: "Your own money", value: uiMoney(car.wallet.cash, { compact: true }), sub: "carried to the next company" },
      { label: "Traits carried", value: `${car.traits.length}` },
    ])}
    ${uiCard("Exit this company", `${(now.offers || []).length ? `<div class="em-cards">${now.offers.map((o) => `
        <div class="em-tile"><div class="em-tile-icon">🤝</div><b>${esc(o.buyer)}</b><span>${esc(o.type)}: ${esc(o.pitch)}</span>
          <em>${uiMoney(o.price, { compact: true })} for the company (${o.premium_pct >= 0 ? "+" : ""}${o.premium_pct}%) · you get ${uiMoney(o.your_payout, { compact: true })}</em>
          <button type="button" class="primary" data-cr-sell="${esc(o.buyer)}">Sell to them</button></div>`).join("")}</div>`
        : `<p class="sx-card-sub">${now.game_over ? "This run has ended." : `Buyers come once the company is ${now.sale_min_month || 6}+ months old and has a valuation.`}</p>`}
      ${now.is_public && !now.game_over ? `<div class="em-actions"><button type="button" data-cr-step>Step down and start again, keeping my shares (${uiMoney(now.step_down_value, { compact: true })})</button></div>` : ""}`,
      { sub: car.rules })}
    <div class="em-grid">
      ${uiCard("Your companies", crRuns(car))}
      ${uiCard("What comes with you", crCarry(car))}
    </div>`;
  host.querySelectorAll("[data-cr-sell]").forEach((b) => b.addEventListener("click", () => {
    const o = now.offers.find((x) => x.buyer === b.dataset.crSell);
    if (confirm(`Sell ${now.company} to ${o.buyer} for ${uiMoney(o.price)}? You get ${uiMoney(o.your_payout)} and this run ends as an exit.`)) emAct("career_sell_company", { buyer: o.buyer });
  }));
  const st = host.querySelector("[data-cr-step]");
  if (st) st.addEventListener("click", () => { if (confirm("Step down as CEO? Your shares stay yours; this run ends and your next company begins.")) emAct("career_step_down"); });
}

// ---------------------------------------------------------------- the end screen

async function renderGameOverExtras(company) {
  const box = document.querySelector("#gameover-screen .gameover-box");
  if (!box) return;
  box.querySelectorAll(".cr-end").forEach((n) => n.remove());
  let car = null, tm = null;
  try { car = await uiGet("/career"); } catch (e) { /* no career */ }
  try { tm = await uiGet("/timemachine"); } catch (e) { /* no checkpoints */ }
  const exit = car && car.last_exit && car.last_exit.company === company.name ? car.last_exit : null;
  const el = document.createElement("div");
  el.className = "cr-end";
  if (exit) {
    const eyebrow = box.querySelector(".charter-eyebrow");
    if (eyebrow) { eyebrow.textContent = "An exit, not an ending"; eyebrow.style.color = "var(--sage)"; }
    const title = document.getElementById("gameover-title");
    if (title) title.textContent = `${company.name} — ${KIND[exit.kind] || "Exit"}`;
  }
  const cps = tm ? tm.checkpoints.filter((m) => m < tm.month) : [];
  el.innerHTML = `${exit ? `<div class="cr-exit">
      <div class="cr-exit-pay"><span>You walk away with</span><b>${uiMoney(exit.payout)}</b></div>
      <p>Your career so far: ${car.exits} exit${car.exits === 1 ? "" : "s"}, ${uiMoney(car.wallet.cash, { compact: true })} of your own money, ${car.traits.length} legacy trait${car.traits.length === 1 ? "" : "s"}.</p>
      ${emSlider("cr-invest", "Put this much into the next company", { min: 0, max: Math.max(0, Math.floor(car.wallet.cash / 1000) * 1000), step: 1000, value: Math.floor(car.wallet.cash / 2 / 1000) * 1000, hint: " " })}
      <div class="em-actions"><button type="button" class="primary" data-cr-next>Start my next company →</button></div>
      <p class="sx-card-sub">Any of the 29 industries. Your traits, reputation, things and foundation come with you; what you don't invest stays in your pocket.</p></div>` : ""}
    ${cps.length ? `<div class="cr-tm"><b>⏳ Or go back in time.</b> <span>Play on from an earlier quarter and take a different road:</span>
      <div class="cr-tm-row">${cps.slice(-6).map((m) => `<button type="button" data-cr-branch="${m}">Month ${m}</button>`).join("")}</div></div>` : ""}`;
  box.insertBefore(el, box.querySelector(".charter-actions"));
  if (exit) {
    const startBtn = document.getElementById("gameover-new-game");
    if (startBtn) startBtn.textContent = "Start fresh (no carry-over)";
    emWireSliders(el, (id, v) => { el.querySelector("#cr-invest-hint").textContent = `Extra starting capital ${uiMoney(v)} · you keep ${uiMoney(car.wallet.cash - v)}`; });
    el.querySelector("[data-cr-next]").addEventListener("click", async () => {
      try {
        const r = await postJSON("/career/next", { invest: emSliderValue(el, "cr-invest") });
        await startNewGame(null);
        const form = document.getElementById("onboarding-form");
        if (form && !form.querySelector(".cr-banner")) {
          const b = document.createElement("div");
          b.className = "cr-banner";
          b.innerHTML = `<b>Serial founder.</b> You're putting ${uiMoney(r.invest)} of your own money in, and bringing ${car.traits.length} legacy trait${car.traits.length === 1 ? "" : "s"} and your reputation. Pick any industry.`;
          form.prepend(b);
        }
      } catch (e) { toast(e.message || "Couldn't start the next company.", true); }
    });
  }
  el.querySelectorAll("[data-cr-branch]").forEach((b) => b.addEventListener("click", async () => {
    if (!confirm(`Go back to month ${b.dataset.crBranch}? This ending is saved to a slot first.`)) return;
    try {
      const r = await postJSON("/timemachine/branch", { month: +b.dataset.crBranch });
      toast(r.message);
      location.reload();
    } catch (e) { toast(e.message || "Couldn't travel back.", true); }
  }));
}

window.renderGameOverExtras = renderGameOverExtras;

export { renderCareerInto, renderGameOverExtras };
